/**
 * D-138: Website → Blog. The list of articles, the editor (one language at a time, Markdown with the
 * server preview), the review and publication steps, and « Proposer un brouillon avec l'IA ».
 * Owner, manager and marketing write and publish; a trainer corrects and reviews the articles of
 * their exams only (the API filters the list and refuses the rest). Unsaved edits are kept on the
 * device (automatic local draft) until they are saved.
 */

import {
  type Article,
  type ArticleRequest,
  getWebsiteBlogListQueryKey,
  useWebsiteBlogAiDraftCreate,
  useWebsiteBlogArchiveCreate,
  useWebsiteBlogCreate,
  useWebsiteBlogList,
  useWebsiteBlogPartialUpdate,
  useWebsiteBlogPublishCreate,
  useWebsiteBlogReopenCreate,
  useWebsiteBlogReviewCreate,
  useWebsiteBlogUnpublishCreate,
} from "@agencyos/api-client";
import {
  Badge,
  type BadgeTone,
  Button,
  GlassCard,
  GlassSheet,
  SegmentedControl,
  SelectField,
  Skeleton,
  TextField,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Archive, CheckCheck, ExternalLink, Plus, Rocket, Sparkles, Trash2, Undo2 } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors, useCurrentRole } from "../../lib/settings";
import { MarkdownEditor } from "./MarkdownEditor";
import "./blog.css";

const CATEGORIES = ["tcf", "tef", "ielts", "german", "immigration", "russia", "visas"] as const;
const EXAMS = ["", "tcf_canada", "tef_canada", "ielts_general", "goethe"] as const;
const STATES = ["all", "draft", "reviewed", "scheduled", "published", "archived"] as const;
type StateFilter = (typeof STATES)[number];
type Lang = "fr" | "en";
const LANGS: Lang[] = ["fr", "en"];
/** Places of the site whose image can be a cover (same keys as apps/website/media.py → SLOTS). */
export const COVER_SLOTS = [
  "service-tcf", "dest-langues", "dest-canada", "home-academy", "home-canada", "home-hero",
  "home-russie", "dest-russie", "dest-visa", "dest-billets", "service-ee", "service-ru", "service-vv",
  "contact", "agency", "simulator",
] as const; // prettier-ignore

type ArticleState = "draft" | "reviewed" | "scheduled" | "published" | "archived";
const TONE: Record<ArticleState, BadgeTone> = {
  draft: "default",
  reviewed: "info",
  scheduled: "warning",
  published: "success",
  archived: "danger",
};

type Texts = Record<string, string>;
interface Source {
  title: string;
  url: string;
}
interface Draft {
  title: Texts;
  slug: Texts;
  summary: Texts;
  body: Texts;
  seo_title: Texts;
  seo_description: Texts;
  sources: Source[];
  category: string;
  exam: string;
  cover_slot: string;
  author_name: string;
}

function texts(value: unknown): Texts {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === "string"));
}

function draftOf(row?: Article): Draft {
  return {
    title: texts(row?.title),
    slug: texts(row?.slug),
    summary: texts(row?.summary),
    body: texts(row?.body),
    seo_title: texts(row?.seo_title),
    seo_description: texts(row?.seo_description),
    sources: (row?.sources ?? []).map((s) => ({ title: s.title, url: s.url })),
    category: row?.category ?? "tcf",
    exam: row?.exam ?? "tcf_canada",
    cover_slot: row?.cover_slot ?? "service-tcf",
    author_name: row?.author_name ?? "",
  };
}

const STORE = "agencyos.blog.draft.";

function loadLocal(key: string): Draft | null {
  try {
    const raw = localStorage.getItem(STORE + key);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

function saveLocal(key: string, value: Draft | null): void {
  try {
    if (value) localStorage.setItem(STORE + key, JSON.stringify(value));
    else localStorage.removeItem(STORE + key);
  } catch {
    /* private mode: the edits simply stay in memory */
  }
}

function titleOf(row: Article, language: string): string {
  const t = texts(row.title);
  return t[language] || t.fr || t.en || "—";
}

function siteOrigin(): string {
  const url = new URL(window.location.origin);
  if (url.port === "5173") url.port = "8001";
  return url.origin;
}

/** A local date-time (Douala) for the « publish at » field → ISO with the offset. */
function toIso(local: string): string | null {
  return local ? `${local}:00+01:00` : null;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: getWebsiteBlogListQueryKey().slice(0, 1) });
}

/* ------------------------------------------------------------------ AI draft */

function AiDraftSheet({ onClose, onCreated }: { onClose: () => void; onCreated: (row: Article) => void }) {
  const { t } = useTranslation();
  const refresh = useRefresh();
  const create = useWebsiteBlogAiDraftCreate({ mutation: { onSuccess: refresh } });
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("tcf");
  const [exam, setExam] = useState("tcf_canada");
  const errors = fieldErrors(create.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      { data: { topic: topic.trim(), category: category as Article["category"], exam: exam as NonNullable<Article["exam"]> } },
      { onSuccess: (r) => r.status === 202 && onCreated(r.data) },
    );
  };
  return (
    <GlassSheet open title={t("blog.ai.title")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <p className="muted">{t("blog.ai.intro")}</p>
        <TextField label={t("blog.ai.topic")} hint={t("blog.ai.topicHint")} value={topic} maxLength={300} error={errors.topic} onChange={(e) => setTopic(e.target.value)} />
        <SelectField label={t("blog.field.category")} value={category} options={CATEGORIES.map((c) => ({ value: c, label: t(`blog.category.${c}`) }))} onChange={(e) => setCategory(e.target.value)} />
        <SelectField label={t("blog.field.exam")} value={exam} options={EXAMS.map((e) => ({ value: e, label: t(`blog.exam.${e || "none"}`) }))} onChange={(e) => setExam(e.target.value)} />
        <ul className="blog-rules muted">
          <li>{t("blog.ai.rule1")}</li>
          <li>{t("blog.ai.rule2")}</li>
          <li>{t("blog.ai.rule3")}</li>
        </ul>
        {errors.detail ? (
          <p role="alert" className="ao-field__error">
            {errors.detail}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" icon={<Sparkles size={16} aria-hidden="true" />} disabled={create.isPending || topic.trim().length < 8}>
            {create.isPending ? t("common.loading") : t("blog.ai.submit")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

/* -------------------------------------------------------------------- Editor */

function SourcesField({ value, onChange, disabled }: { value: Source[]; onChange: (v: Source[]) => void; disabled: boolean }) {
  const { t } = useTranslation();
  return (
    <fieldset className="stack-sm blog-sources">
      <legend className="ao-field__label">{t("blog.field.sources")}</legend>
      <p className="ao-field__hint">{t("blog.field.sourcesHint")}</p>
      {value.map((source, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place, never re-ordered
        <div key={index} className="blog-sources__row">
          <TextField label={t("blog.field.sourceTitle", { n: index + 1 })} value={source.title} disabled={disabled} onChange={(e) => onChange(value.map((s, i) => (i === index ? { ...s, title: e.target.value } : s)))} />
          <TextField label={t("blog.field.sourceUrl", { n: index + 1 })} type="url" value={source.url} disabled={disabled} onChange={(e) => onChange(value.map((s, i) => (i === index ? { ...s, url: e.target.value } : s)))} />
          {disabled ? null : (
            <Button variant="ghost" size="sm" icon={<Trash2 size={16} aria-hidden="true" />} onClick={() => onChange(value.filter((_, i) => i !== index))}>
              {t("common.remove")}
            </Button>
          )}
        </div>
      ))}
      {disabled ? null : (
        <Button variant="secondary" size="sm" icon={<Plus size={16} aria-hidden="true" />} onClick={() => onChange([...value, { title: "", url: "" }])}>
          {t("blog.field.addSource")}
        </Button>
      )}
    </fieldset>
  );
}

function Editor({ row, onClose }: { row?: Article; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const role = useCurrentRole("website");
  const publisher = role === "owner" || role === "manager";
  const trainer = role === "trainer";
  const refresh = useRefresh();
  const storeKey = row?.id ?? "new";
  const stored = useMemo(() => loadLocal(storeKey), [storeKey]);
  const [draft, setDraft] = useState<Draft>(() => stored ?? draftOf(row));
  const [restored] = useState(Boolean(stored));
  const [lang, setLang] = useState<Lang>(i18n.language === "en" ? "en" : "fr");
  const [current, setCurrent] = useState<Article | undefined>(row);
  const [when, setWhen] = useState("");
  const after = { mutation: { onSuccess: refresh } };
  const create = useWebsiteBlogCreate(after);
  const update = useWebsiteBlogPartialUpdate(after);
  const review = useWebsiteBlogReviewCreate(after);
  const publish = useWebsiteBlogPublishCreate(after);
  const unpublish = useWebsiteBlogUnpublishCreate(after);
  const archive = useWebsiteBlogArchiveCreate(after);
  const reopen = useWebsiteBlogReopenCreate(after);
  const errors = { ...fieldErrors(create.error), ...fieldErrors(update.error) };
  const actionError = [review, publish, unpublish, archive, reopen].map((m) => fieldErrors(m.error).detail).find(Boolean);
  const locked = !publisher && !trainer;
  const pending = ai(current);

  useEffect(() => {
    saveLocal(storeKey, draft);
  }, [draft, storeKey]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const setText = (key: "title" | "slug" | "summary" | "body" | "seo_title" | "seo_description", value: string) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], [lang]: value } }));

  const payload = (): ArticleRequest => {
    const sources = draft.sources.filter((s) => s.title.trim() && s.url.trim());
    const text = { title: draft.title, summary: draft.summary, body: draft.body, seo_title: draft.seo_title, seo_description: draft.seo_description, sources };
    // A trainer corrects the texts only (the API refuses any other field from them).
    if (trainer) return text as ArticleRequest;
    return { ...text, slug: draft.slug, category: draft.category as Article["category"], exam: draft.exam as NonNullable<Article["exam"]>, cover_slot: draft.cover_slot, author_name: draft.author_name };
  };
  const onSave = (event: FormEvent) => {
    event.preventDefault();
    const done = (status: number, data: Article) => {
      if (status === 200 || status === 201) {
        saveLocal(storeKey, null);
        setCurrent(data);
      }
    };
    if (current) update.mutate({ id: current.id, data: payload() }, { onSuccess: (r) => done(r.status, r.data as Article) });
    else create.mutate({ data: payload() }, { onSuccess: (r) => done(r.status, r.data as Article) });
  };
  /** The result of a status action becomes the article shown. */
  const shown = { onSuccess: (r: { status: number; data: unknown }) => r.status === 200 && setCurrent(r.data as Article) };
  const state = (current?.state ?? "draft") as ArticleState;
  const url = current?.urls?.[lang];
  const counter = (value: string, max: number) => t("blog.editor.counter", { n: value.length, max });

  return (
    <form className="stack blog-editor" onSubmit={onSave} noValidate>
      <div className="row blog-editor__top">
        <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={onClose}>
          {t("blog.back")}
        </Button>
        <Badge tone={TONE[state]} dot>
          {t(`blog.state.${state}`)}
        </Badge>
        {current?.ai_generated ? <Badge tone="accent">{t("blog.ai.badge")}</Badge> : null}
        {current?.reviewed_by_name ? <span className="muted">{t("blog.reviewedBy", { name: current.reviewed_by_name })}</span> : null}
        {url && (state === "published" || state === "scheduled") ? (
          <a className="link" href={`${siteOrigin()}${url}`} target="_blank" rel="noreferrer">
            <ExternalLink size={16} aria-hidden="true" /> {t("website.viewPage")}
          </a>
        ) : null}
      </div>
      {restored ? <p className="muted" role="status">{t("blog.editor.restored")}</p> : null}
      {pending ? <p className="muted" role="status">{t("blog.ai.pending")}</p> : null}
      {current?.ai_error ? (
        <p role="alert" className="ao-field__error">
          {current.ai_error}
        </p>
      ) : null}

      <GlassCard as="section" className="stack">
        <SegmentedControl label={t("blog.editor.language")} value={lang} onChange={setLang} options={LANGS.map((l) => ({ value: l, label: t(`catalog.language.${l}`) }))} />
        <TextField label={t("blog.field.title")} lang={lang} value={draft.title[lang] ?? ""} maxLength={200} disabled={locked} error={errors.title} onChange={(e) => setText("title", e.target.value)} />
        {trainer ? null : (
          <TextField label={t("blog.field.slug")} hint={t("blog.field.slugHint")} lang={lang} value={draft.slug[lang] ?? ""} maxLength={80} disabled={locked} error={errors.slug} onChange={(e) => setText("slug", e.target.value)} />
        )}
        <TextField label={t("blog.field.summary")} lang={lang} value={draft.summary[lang] ?? ""} maxLength={300} disabled={locked} error={errors.summary} onChange={(e) => setText("summary", e.target.value)} />
        <MarkdownEditor label={t("blog.field.body")} lang={lang} value={draft.body[lang] ?? ""} disabled={locked} error={errors.body} onChange={(v) => setText("body", v)} />
        <div className="grid-2">
          <TextField label={t("blog.field.seoTitle")} hint={counter(draft.seo_title[lang] ?? "", 60)} lang={lang} value={draft.seo_title[lang] ?? ""} maxLength={70} disabled={locked} onChange={(e) => setText("seo_title", e.target.value)} />
          <TextField label={t("blog.field.seoDescription")} hint={counter(draft.seo_description[lang] ?? "", 155)} lang={lang} value={draft.seo_description[lang] ?? ""} maxLength={170} disabled={locked} onChange={(e) => setText("seo_description", e.target.value)} />
        </div>
      </GlassCard>

      <GlassCard as="section" className="stack">
        <h3>{t("blog.editor.settings")}</h3>
        <div className="grid-2">
          <SelectField label={t("blog.field.category")} value={draft.category} disabled={!publisher} options={CATEGORIES.map((c) => ({ value: c, label: t(`blog.category.${c}`) }))} onChange={(e) => set("category", e.target.value)} />
          <SelectField label={t("blog.field.exam")} value={draft.exam} disabled={!publisher} options={EXAMS.map((e) => ({ value: e, label: t(`blog.exam.${e || "none"}`) }))} onChange={(e) => set("exam", e.target.value)} />
          <SelectField label={t("blog.field.cover")} hint={t("blog.field.coverHint")} value={draft.cover_slot} disabled={!publisher} options={COVER_SLOTS.map((s) => ({ value: s, label: t(`websiteMedia.slot.${s}`) }))} onChange={(e) => set("cover_slot", e.target.value)} />
          <TextField label={t("blog.field.author")} hint={t("blog.field.authorHint")} value={draft.author_name} maxLength={120} disabled={!publisher} onChange={(e) => set("author_name", e.target.value)} />
        </div>
        <SourcesField value={draft.sources} disabled={locked} onChange={(v) => set("sources", v)} />
      </GlassCard>

      <GlassCard as="section" className="stack">
        <h3>{t("blog.editor.publication")}</h3>
        <p className="muted">{t(`blog.stateHelp.${state}`)}</p>
        {actionError ? (
          <p role="alert" className="ao-field__error">
            {actionError}
          </p>
        ) : null}
        <div className="row blog-actions">
          {locked ? null : (
            <Button type="submit" disabled={create.isPending || update.isPending || pending}>
              {create.isPending || update.isPending ? t("common.loading") : t("common.save")}
            </Button>
          )}
          {current && (publisher || trainer) && state === "draft" ? (
            <Button variant="secondary" icon={<CheckCheck size={16} aria-hidden="true" />} disabled={review.isPending || pending} onClick={() => current && review.mutate({ id: current.id }, shown)}>
              {t("blog.action.review")}
            </Button>
          ) : null}
          {current && publisher && (state === "reviewed" || state === "scheduled" || state === "published") ? (
            <>
              <TextField label={t("blog.action.when")} hint={t("blog.action.whenHint")} type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
              <Button icon={<Rocket size={16} aria-hidden="true" />} disabled={publish.isPending} onClick={() => current && publish.mutate({ id: current.id, data: { publish_at: toIso(when) } }, shown)}>
                {when ? t("blog.action.schedule") : t("blog.action.publish")}
              </Button>
            </>
          ) : null}
          {current && publisher && (state === "published" || state === "scheduled") ? (
            <Button variant="secondary" icon={<Undo2 size={16} aria-hidden="true" />} onClick={() => current && unpublish.mutate({ id: current.id }, shown)}>
              {t("blog.action.unpublish")}
            </Button>
          ) : null}
          {current && publisher && state !== "draft" ? (
            <Button variant="ghost" onClick={() => current && reopen.mutate({ id: current.id }, shown)}>
              {t("blog.action.reopen")}
            </Button>
          ) : null}
          {current && publisher && state !== "archived" ? (
            <Button variant="ghost" icon={<Archive size={16} aria-hidden="true" />} onClick={() => current && archive.mutate({ id: current.id }, shown)}>
              {t("blog.action.archive")}
            </Button>
          ) : null}
        </div>
      </GlassCard>
    </form>
  );
}

function ai(row?: Article): boolean {
  return row?.ai_status === "pending";
}

/* ---------------------------------------------------------------------- List */

export function BlogTab() {
  const { t, i18n } = useTranslation();
  const role = useCurrentRole("website");
  const publisher = role === "owner" || role === "manager";
  const [filter, setFilter] = useState<StateFilter>("all");
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Article | "new" | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const list = useWebsiteBlogList({ category: category || undefined, q: query.trim() || undefined });
  const rows = list.data?.status === 200 ? list.data.data.results : [];
  const shown = filter === "all" ? rows : rows.filter((r) => r.state === filter);
  const queryClient = useQueryClient();
  const busy = rows.some(ai);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = window.setInterval(() => void queryClient.invalidateQueries({ queryKey: getWebsiteBlogListQueryKey().slice(0, 1) }), 5000);
    return () => window.clearInterval(timer);
  }, [busy, queryClient]);

  if (editing) {
    const row = editing === "new" ? undefined : (rows.find((r) => r.id === editing.id) ?? editing);
    return <Editor key={editing === "new" ? "new" : editing.id} row={row} onClose={() => setEditing(null)} />;
  }
  return (
    <div className="stack">
      <GlassCard as="section" className="stack">
        <div className="section-title">
          <div>
            <h2>{t("blog.title")}</h2>
            <p className="muted">{role === "trainer" ? t("blog.introTrainer") : t("blog.intro")}</p>
          </div>
          {publisher ? (
            <div className="row">
              <Button variant="secondary" icon={<Sparkles size={16} aria-hidden="true" />} onClick={() => setAiOpen(true)}>
                {t("blog.ai.open")}
              </Button>
              <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setEditing("new")}>
                {t("blog.new")}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="blog-filters">
          <SegmentedControl label={t("blog.filter.state")} value={filter} onChange={setFilter} options={STATES.map((s) => ({ value: s, label: t(s === "all" ? "blog.filter.all" : `blog.state.${s}`) }))} />
          <SelectField label={t("blog.field.category")} value={category} options={[{ value: "", label: t("blog.filter.allCategories") }, ...CATEGORIES.map((c) => ({ value: c, label: t(`blog.category.${c}`) }))]} onChange={(e) => setCategory(e.target.value)} />
          <TextField label={t("blog.filter.search")} type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {list.isPending ? (
          <Skeleton style={{ height: 200 }} />
        ) : shown.length ? (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("blog.field.title")}</th>
                  <th>{t("blog.field.category")}</th>
                  <th>{t("blog.list.languages")}</th>
                  <th>{t("blog.list.state")}</th>
                  <th>{t("blog.list.date")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button type="button" className="link" onClick={() => setEditing(row)}>
                        {titleOf(row, i18n.language)}
                      </button>
                      {row.ai_generated ? <Badge tone="accent">{t("blog.ai.badge")}</Badge> : null}
                    </td>
                    <td>{t(`blog.category.${row.category}`)}</td>
                    <td className="ao-num">{row.languages.map((l) => l.toUpperCase()).join(" · ") || "—"}</td>
                    <td>
                      <Badge tone={TONE[row.state as ArticleState]} dot>
                        {ai(row) ? t("blog.ai.pendingShort") : t(`blog.state.${row.state}`)}
                      </Badge>
                    </td>
                    <td className="ao-num">{row.publish_at ? new Date(row.publish_at).toLocaleDateString(i18n.language, { timeZone: "Africa/Douala" }) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("blog.empty")}</p>
        )}
      </GlassCard>
      {aiOpen ? (
        <AiDraftSheet
          onClose={() => setAiOpen(false)}
          onCreated={(row) => {
            setAiOpen(false);
            setEditing(row);
          }}
        />
      ) : null}
    </div>
  );
}
